import { useState, memo, useMemo, useEffect, useCallback } from "react";
import { useForm } from "react-hook-form";
import { ClipboardPen } from "lucide-react";
import {
  Box,
  Stepper,
  Step,
  StepLabel,
  Button,
  Card,
  Typography,
  CircularProgress,
} from "@mui/material";
import { Check } from "@mui/icons-material";
import { useTheme, useMediaQuery } from "@mui/material";

import VehicleFinancierDetailsForm from "./forms/VehicleFinancierDetailsForm";
import { BackButton, NextButton } from "../../components/buttons";
import DriverDetailsForm from "./forms/DriverDetailsForm";
import { EmiWrapperComponent } from "../emiCalculator";
import PreviewStep from "./preview/PreviewStep";
import SuccessDialog from "./SuccessDialog";
import {
  DOCUMENTS_BY_FINANCIER,
  FINANCIER_TYPES,
} from "./constants/documents.config";
import { usePreOnboarding } from "./usePreOnboarding";
import { uploadAllPreOnboardingFiles } from "../../utils/uploadPreOnboardingFiles";
import { createDriverPreOnboarding } from "../../api/modules/preOnboarding";
import FullScreenLoader from "../../components/fullScreenLoader";
import {
  driverDetailsSchema,
  getVehicleFinancierSchema,
} from "./forms/formSchema";
import { useAuth } from "../../contexts/AuthContext";
import DocumentSection from "./financierSections/DocumentSection";

const flattenDocuments = (sections) => {
  return sections.flatMap((section) =>
    (section.documents || []).map((doc) => ({
      id: doc.id,
      name: doc.name,
      uploadType: doc.uploadType,
      optional: doc.requirementType === "OPTIONAL",
      requirementType: doc.requirementType,
      rule: doc.rule,
    })),
  );
};

const shouldRequireDocument = (rule, context) => {
  if (!rule) return true;

  if (rule.anyOf) {
    return rule.anyOf.some((condition) => {
      if (condition.field === "cibilScore" && condition.lessThan) {
        return context.cibilScore && context.cibilScore < condition.lessThan;
      }
      if (
        condition.field === "ageException" &&
        condition.equals !== undefined
      ) {
        return context.ageException === condition.equals;
      }
      if (condition.field === "vehicleFinanced" && condition.equals) {
        return context.vehicleFinanced === condition.equals;
      }
      return false;
    });
  }

  if (rule.field && rule.equals !== undefined) {
    if (rule.field === "vehicleFinanced") {
      return context.vehicleFinanced === rule.equals;
    }
    if (rule.field === "ageException") {
      return context.ageException === rule.equals;
    }
    if (rule.field === "cibilScore" && rule.lessThan) {
      return context.cibilScore && context.cibilScore < rule.lessThan;
    }
  }

  return true;
};

const getRequiredDocuments = (financierType, context) => {
  const sections = DOCUMENTS_BY_FINANCIER[financierType];
  if (!sections) return [];

  return flattenDocuments(sections).filter(
    (doc) => !doc.optional && shouldRequireDocument(doc.rule, context),
  );
};

const getFormSteps = (vehicleFinanced) => {
  const baseSteps = ["Driver Details"];

  if (vehicleFinanced === "Yes") {
    baseSteps.push("Vehicle Financier");
  }

  baseSteps.push("Documents", "Review & Submit");

  return baseSteps;
};

const PreOnboarding = memo(
  ({
    setActiveStep,
    emiResult,
    financierType,
    onSubmitSuccess,
    resetCalculator,
  }) => {
    const { user } = useAuth();
    const userEmail = user?.email || "Unknown User";

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const {
      preOnboardingState,
      updatePreOnboardingState,
      resetPreOnboardingState,
    } = usePreOnboarding();

    const [isSubmitting, setIsSubmitting] = useState(false);

    const [successDialog, setSuccessDialog] = useState({
      open: false,
      prn: "",
      driverName: "",
    });

    const {
      register,
      control,
      formState: { errors },
      watch,
      reset,
    } = useForm({
      mode: "onBlur",
      defaultValues: preOnboardingState.formValues,
    });

    const formValues = watch();
    const vehicleFinanced = watch("vehicleFinanced");

    const FORM_STEPS = useMemo(
      () => getFormSteps(vehicleFinanced),
      [vehicleFinanced],
    );

    useEffect(() => {
      const hasChanged =
        JSON.stringify(preOnboardingState.formValues) !==
        JSON.stringify(formValues);
      if (hasChanged) {
        updatePreOnboardingState({
          formValues,
        });
      }
    }, [formValues, updatePreOnboardingState, preOnboardingState.formValues]);

    const handleDocumentUpload = useCallback(
      (documentId, files, side = null) => {
        const uploadedFiles = preOnboardingState.uploadedFiles;
        const current = uploadedFiles[documentId] || {};
        let updatedFiles = { ...uploadedFiles };

        if (side === "front" || side === "back") {
          updatedFiles[documentId] = {
            ...current,
            [side]: files[0],
          };
        } else if (side === "multiple") {
          updatedFiles[documentId] = {
            ...current,
            multiple: [...(current.multiple || []), ...files],
          };
        } else {
          updatedFiles[documentId] = {
            ...current,
            single: files[0],
          };
        }

        updatePreOnboardingState({ uploadedFiles: updatedFiles });

        if (preOnboardingState.documentErrors[documentId]) {
          updatePreOnboardingState({
            documentErrors: Object.fromEntries(
              Object.entries(preOnboardingState.documentErrors).filter(
                ([key]) => key !== documentId,
              ),
            ),
          });
        }
      },
      [
        preOnboardingState.uploadedFiles,
        preOnboardingState.documentErrors,
        updatePreOnboardingState,
      ],
    );

    const getDocumentsStepIndex = () => {
      return vehicleFinanced === "Yes" ? 2 : 1;
    };

    const getPreviewStepIndex = () => {
      return vehicleFinanced === "Yes" ? 3 : 2;
    };

    const documentContext = {
      cibilScore: emiResult?.cibilScore ?? null,
      vehicleFinanced: formValues.vehicleFinanced,
      ageException: formValues.age > 55 || formValues.age < 18,
    };

    const validateDocuments = useCallback(() => {
      const requiredDocs = getRequiredDocuments(financierType, documentContext);
      const newErrors = {};

      requiredDocs.forEach((doc) => {
        const uploadedFile = preOnboardingState.uploadedFiles[doc.id];

        if (!uploadedFile) {
          newErrors[doc.id] = `${doc.name} is required`;
        } else if (doc.uploadType === "both") {
          if (!uploadedFile.front || !uploadedFile.back) {
            newErrors[doc.id] =
              `Both front and back of ${doc.name} are required`;
          }
        } else if (doc.uploadType === "multiple") {
          if (!uploadedFile.multiple || uploadedFile.multiple.length === 0) {
            newErrors[doc.id] = `At least one file for ${doc.name} is required`;
          }
        } else {
          if (!uploadedFile.single) {
            newErrors[doc.id] = `${doc.name} is required`;
          }
        }
      });

      updatePreOnboardingState({ documentErrors: newErrors });
      return Object.keys(newErrors).length === 0;
    }, [
      financierType,
      documentContext,
      preOnboardingState.uploadedFiles,
      updatePreOnboardingState,
    ]);

    const transformDocumentsToPayload = (uploadedFiles, uploadedDocuments) => {
      const payload = {};

      Object.entries(uploadedFiles).forEach(([documentId, files]) => {
        const uploaded = uploadedDocuments[documentId];

        // Handle BOTH (front + back)
        if (files.front || files.back) {
          payload[documentId] = {
            frontUrl: uploaded?.frontUrl || files.front?.url,
            backUrl: uploaded?.backUrl || files.back?.url,
          };
        }
        // Handle MULTIPLE files
        else if (files.multiple && files.multiple.length > 0) {
          payload[documentId] = {
            urls: uploaded?.urls || files.multiple.map((f) => f.url),
          };
        }
        // Handle SINGLE file
        else if (files.single) {
          payload[documentId] = {
            url: uploaded?.url || files.single.url,
          };
        }
      });

      return payload;
    };

    const handleNext = useCallback(async () => {
      if (isSubmitting) return;

      const documentsStep = getDocumentsStepIndex();
      const previewStep = getPreviewStepIndex();
      const currentStep = preOnboardingState.activeInnerStep;

      if (currentStep === 0) {
        try {
          await driverDetailsSchema.validate(formValues, { abortEarly: false });
          updatePreOnboardingState({ validationErrors: {} });
          updatePreOnboardingState({ activeInnerStep: currentStep + 1 });
        } catch (validationError) {
          const errors = {};
          validationError.inner?.forEach((err) => {
            errors[err.path] = err.message;
          });
          updatePreOnboardingState({ validationErrors: errors });
          return;
        }
      } else if (currentStep === 1 && vehicleFinanced === "Yes") {
        try {
          await getVehicleFinancierSchema(vehicleFinanced).validate(
            formValues,
            {
              abortEarly: false,
            },
          );
          updatePreOnboardingState({ validationErrors: {} });
          updatePreOnboardingState({ activeInnerStep: currentStep + 1 });
        } catch (validationError) {
          const errors = {};
          validationError.inner?.forEach((err) => {
            errors[err.path] = err.message;
          });
          updatePreOnboardingState({ validationErrors: errors });
          return;
        }
      } else if (currentStep === documentsStep) {
        if (validateDocuments()) {
          updatePreOnboardingState({ activeInnerStep: currentStep + 1 });
        }
      } else if (currentStep === previewStep) {
        setIsSubmitting(true);

        try {
          const uploadedDocuments = await uploadAllPreOnboardingFiles(
            preOnboardingState.uploadedFiles,
            formValues.mobileNumber,
          );

          // Transform documents to clean payload format
          const documentsPayload = transformDocumentsToPayload(
            preOnboardingState.uploadedFiles,
            uploadedDocuments,
          );

          const driverDetails = {
            driverName: formValues.driverName,
            mobileNumber: formValues.mobileNumber,
            alternateMobileNumber: formValues.alternateMobileNumber,
            residentialAddress: formValues.residentialAddress,
            age: formValues.age,
            vehicleFinanced: formValues.vehicleFinanced,
            city: emiResult?.city || "",
          };

          if (formValues.otherInformation) {
            driverDetails.otherInformation = formValues.otherInformation;
          }

          // Prepare clean payload
          const payload = {
            financierType,
            financierInternal: emiResult?.financierInternal || null,
            driverDetails,
            vehicleFinancierDetails:
              formValues.vehicleFinanced === "Yes"
                ? {
                    vehicleFinancierName: formValues.vehicleFinancierName,
                    vehicleFinancierAddress: formValues.vehicleFinancierAddress,
                    vehicleFinancierPhone: formValues.vehicleFinancierPhone,
                  }
                : null,

            documents: documentsPayload,
            loanCalculationSummary: emiResult,
            createdBy: userEmail,
          };

          // Call API
          const response = await createDriverPreOnboarding(payload);

          const prn = response?.data?.prn || response?.prn;

          // Success dialog
          setSuccessDialog({
            open: true,
            prn,
            driverName: formValues.driverName,
          });

          onSubmitSuccess?.({
            prn,
            financierType,
            formData: formValues,
            // uploadedFiles: documentsPayload,
          });
        } catch (error) {
          console.error("Pre-onboarding submit failed:", error);

          alert(
            error?.response?.data?.message ||
              error.message ||
              "Failed to submit pre-onboarding",
          );
        } finally {
          setIsSubmitting(false);
        }
      }
    }, [
      isSubmitting,
      preOnboardingState.activeInnerStep,
      preOnboardingState.uploadedFiles,
      formValues,
      vehicleFinanced,
      financierType,
      updatePreOnboardingState,
      validateDocuments,
      onSubmitSuccess,
    ]);

    const handleBack = useCallback(() => {
      if (isSubmitting) return;

      const currentStep = preOnboardingState.activeInnerStep;
      if (currentStep === 0) {
        setActiveStep((prev) => prev - 1);
      } else {
        updatePreOnboardingState({ activeInnerStep: currentStep - 1 });
      }
    }, [
      isSubmitting,
      preOnboardingState.activeInnerStep,
      setActiveStep,
      updatePreOnboardingState,
    ]);

    const handleCloseSuccess = useCallback(() => {
      setSuccessDialog({ open: false, prn: "", driverName: "" });
      resetPreOnboardingState();
      resetCalculator();
      reset();
    }, [reset]);

    const documentsStep = getDocumentsStepIndex();
    const previewStep = getPreviewStepIndex();
    const activeInnerStep = preOnboardingState.activeInnerStep;

    if (
      !financierType ||
      !Object.values(FINANCIER_TYPES).includes(financierType)
    ) {
      return (
        <Box sx={{ p: 4, textAlign: "center" }}>
          <Typography variant="h6" sx={{ color: "#dc2626" }}>
            Invalid financier type provided.
          </Typography>
        </Box>
      );
    }

    return (
      <EmiWrapperComponent
        title="Driver Pre-Onboarding"
        subTitle="Fill The All Required Input"
        icon={<ClipboardPen size={50} />}
        actions={[
          <BackButton onClick={handleBack} disabled={isSubmitting} />,

          activeInnerStep === FORM_STEPS.length - 1 ? (
            <Button
              onClick={handleNext}
              variant="contained"
              size="large"
              startIcon={
                isSubmitting ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <Check />
                )
              }
              disabled={isSubmitting}
              sx={{
                bgcolor: "#059669",
                "&:hover": { bgcolor: "#047857" },
                "&:disabled": { bgcolor: "#94a3b8" },
                order: { xs: 2, sm: 1 },
              }}
            >
              {isSubmitting
                ? "Submitting..."
                : isMobile
                  ? "Submit"
                  : "Submit Form"}
            </Button>
          ) : (
            <NextButton onClick={handleNext} disabled={isSubmitting} />
          ),
        ]}
      >
        <Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              mb: 2,
              flexWrap: "wrap",
            }}
          >
            <Typography variant="body2" sx={{ color: "#6b7280" }}>
              Financier: <strong>{financierType}</strong>
            </Typography>
            <Typography variant="body2" sx={{ color: "#6b7280" }}>
              Step {activeInnerStep + 1} of {FORM_STEPS.length}
            </Typography>
          </Box>

          <Card sx={{ mb: 4 }}>
            <Box sx={{ p: 3 }}>
              <Stepper activeStep={activeInnerStep}>
                {FORM_STEPS.map((label, index) => (
                  <Step key={index}>
                    <StepLabel>{!isMobile ? label : ""}</StepLabel>
                  </Step>
                ))}
              </Stepper>
            </Box>
          </Card>

          <Card sx={{ mb: 4 }}>
            <Box sx={{ p: { xs: 2, sm: 3 } }}>
              {activeInnerStep === 0 && (
                <DriverDetailsForm
                  register={register}
                  errors={{ ...errors, ...preOnboardingState.validationErrors }}
                  watch={watch}
                  control={control}
                />
              )}

              {activeInnerStep === 1 && vehicleFinanced === "Yes" && (
                <VehicleFinancierDetailsForm
                  register={register}
                  errors={{ ...errors, ...preOnboardingState.validationErrors }}
                  watch={watch}
                />
              )}

              {activeInnerStep === documentsStep && (
                <Box>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 600, mb: 3, color: "#1f2937" }}
                  >
                    Upload Required Documents
                  </Typography>

                  <DocumentSection
                    financierType={financierType}
                    context={documentContext}
                    uploadedFiles={preOnboardingState.uploadedFiles}
                    onDocumentUpload={handleDocumentUpload}
                    documentErrors={preOnboardingState.documentErrors}
                  />
                </Box>
              )}

              {activeInnerStep === previewStep && (
                <PreviewStep
                  formData={formValues}
                  uploadedFiles={preOnboardingState.uploadedFiles}
                  financierType={financierType}
                />
              )}

              {isSubmitting && (
                <FullScreenLoader message="Uploading files and submitting form..." />
              )}
            </Box>
          </Card>
        </Box>

        <SuccessDialog
          open={successDialog.open}
          onClose={handleCloseSuccess}
          prn={successDialog.prn}
          driverName={successDialog?.driverName || ""}
        />
      </EmiWrapperComponent>
    );
  },
);

export default PreOnboarding;
